import { useTranslations } from 'next-intl';
import type { BadgeKey } from '@/lib/product-sections';

interface TrustBadgesProps {
  /**
   * Which promises to draw, from `sectionBadges`. Torsbo's feedback ("Hemsidan
   * 2.0" p.3) is that these were being claimed for products they do not apply
   * to — a weapon has none of them — so the caller decides and nothing is
   * hardcoded here. Which two a shippable product carries changed again in
   * "Hemsidan 3.0" p.3; see BadgeKey.
   */
  badges: BadgeKey[];
  className?: string;
}

/** The mark drawn beside each promise. */
const ICONS: Record<BadgeKey, React.ReactNode> = {
  fastShipping: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
  ),
  securePayment: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
    />
  ),
  helpWithLicenseApplication: (
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
    />
  ),
};

export function TrustBadges({ badges, className }: TrustBadgesProps) {
  const t = useTranslations('product');

  // A licensed weapon claims only licence help, and an empty list is a real
  // outcome rather than a bug — draw nothing instead of an empty flex row.
  if (badges.length === 0) return null;

  return (
    <div className={`flex flex-wrap gap-6 ${className ?? ''}`}>
      {badges.map((badge) => (
        <div key={badge} className="flex items-center gap-2 text-sm text-muted">
          <svg
            className="h-5 w-5 text-primary-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            {ICONS[badge]}
          </svg>
          {t(badge)}
        </div>
      ))}
    </div>
  );
}
